import React, { useState, useEffect } from 'react';
import { multiFactor, TotpMultiFactorGenerator, sendEmailVerification } from 'firebase/auth';
import QRCode from 'qrcode';
import { CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

function UnverifiedEmail({ currentUser }) {
  const [sent,    setSent]    = useState(false);
  const [sending, setSending] = useState(false);
  const [err,     setErr]     = useState('');

  const handleSend = async () => {
    setSending(true);
    setErr('');
    try {
      await sendEmailVerification(currentUser);
      setSent(true);
    } catch (e) {
      setErr('Could not send verification email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-1" role="status" aria-live="polite">
        <p className="text-sm text-green-700 font-medium">Verification email sent.</p>
        <p className="text-xs text-gray-600">
          Click the link in the email, then sign out and back in to continue 2FA setup.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-amber-700">
        Your email address needs to be verified before you can enable 2FA.
      </p>
      {err && <p className="text-xs text-red-600" role="alert">{err}</p>}
      <button
        onClick={handleSend}
        disabled={sending}
        className="text-sm text-teal-600 font-medium hover:underline disabled:opacity-50"
      >
        {sending ? 'Sending…' : 'Send verification email'}
      </button>
    </div>
  );
}

export default function MFASetupFlow({ onComplete, onCancel }) {
  const { currentUser } = useAuth();
  const [step,        setStep]        = useState('loading'); // loading|qr|verify|done|error
  const [qrDataUrl,   setQrDataUrl]   = useState('');
  const [secretKey,   setSecretKey]   = useState('');
  const [totpSecret,  setTotpSecret]  = useState(null);
  const [showKey,     setShowKey]     = useState(false);
  const [code,        setCode]        = useState('');
  const [error,       setError]       = useState('');
  const [submitting,  setSubmitting]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const mfaUser = multiFactor(currentUser);
        const session = await mfaUser.getSession();
        const secret  = await TotpMultiFactorGenerator.generateSecret(session);
        if (cancelled) return;
        const qrUrl  = secret.generateQrCodeUrl(currentUser.email, 'STX Connect');
        const imgUrl = await QRCode.toDataURL(qrUrl, { width: 200, margin: 1 });
        if (cancelled) return;
        setTotpSecret(secret);
        setSecretKey(secret.secretKey);
        setQrDataUrl(imgUrl);
        setStep('qr');
      } catch (e) {
        if (cancelled) return;
        if (e.code === 'auth/requires-recent-login') {
          setStep('reauth');
        } else if (e.code === 'auth/unverified-email') {
          setStep('unverified');
        } else {
          setStep('error');
        }
      }
    }
    init();
    return () => { cancelled = true; };
  }, [currentUser]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        code.replace(/\s/g, '')
      );
      await multiFactor(currentUser).enroll(assertion, 'Authenticator App');
      await currentUser.reload();
      setStep('done');
      setTimeout(() => onComplete?.(), 1200);
    } catch (err) {
      setError(
        err.code === 'auth/invalid-verification-code'
          ? 'Incorrect code — check your authenticator app and try again.'
          : 'Could not verify code. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'loading') {
    return <p className="text-sm text-gray-600 py-2">Setting up…</p>;
  }

  if (step === 'error') {
    return <p className="text-sm text-red-600 py-2">Could not start 2FA setup. Please try again.</p>;
  }

  if (step === 'reauth') {
    return (
      <p className="text-sm text-amber-700 py-2">
        For security, please sign out and sign back in before setting up 2FA.
      </p>
    );
  }

  if (step === 'unverified') {
    return <UnverifiedEmail currentUser={currentUser} />;
  }

  if (step === 'done') {
    return (
      <div className="flex items-center gap-2 text-green-600 py-2" role="status" aria-live="polite">
        <CheckCircle2 size={16} aria-hidden="true" />
        <span className="text-sm font-medium">2FA enabled successfully.</span>
      </div>
    );
  }

  if (step === 'qr') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          Scan the QR code below with an authenticator app (Google Authenticator, Authy, or similar), then click Next.
        </p>
        <div className="flex justify-center">
          <img
            src={qrDataUrl}
            alt="QR code to scan with your authenticator app"
            width={200}
            height={200}
            className="border border-gray-200 rounded-lg p-1"
          />
        </div>
        {/* Manual key fallback */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowKey(v => !v)}
            className="text-xs text-teal-600 hover:underline flex items-center gap-1 mx-auto"
          >
            {showKey ? <EyeOff size={11} aria-hidden="true" /> : <Eye size={11} aria-hidden="true" />}
            {showKey ? 'Hide' : 'Can\'t scan? Show'} setup key
          </button>
          {showKey && (
            <p className="mt-1.5 text-xs font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1.5 tracking-widest text-gray-700 select-all break-all">
              {secretKey}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          {onCancel && (
            <button type="button" onClick={onCancel}
              className="px-3 py-1.5 text-sm text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50">
              Cancel
            </button>
          )}
          <button type="button" onClick={() => setStep('verify')}
            className="px-4 py-1.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700">
            Next — enter code
          </button>
        </div>
      </div>
    );
  }

  // step === 'verify'
  return (
    <form onSubmit={handleVerify} className="space-y-3">
      <p className="text-sm text-gray-700">
        Enter the 6-digit code from your authenticator app to confirm setup.
      </p>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={7}
        value={code}
        onChange={e => setCode(e.target.value.replace(/[^0-9 ]/g, ''))}
        placeholder="000 000"
        className="w-full border border-gray-300 rounded-xl px-4 py-3 text-center text-xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder:text-gray-500"
        aria-label="Authenticator code"
        required
        aria-required="true"
        aria-invalid={!!error}
        aria-describedby={error ? 'mfa-verify-error' : undefined}
      />
      {error && (
        <p id="mfa-verify-error" role="alert" className="text-sm text-red-600">{error}</p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => { setStep('qr'); setError(''); setCode(''); }}
          className="px-3 py-1.5 text-sm text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50">
          ← Back
        </button>
        <button
          type="submit"
          disabled={submitting || code.replace(/\s/g, '').length !== 6}
          className="px-4 py-1.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
        >
          {submitting ? 'Verifying…' : 'Enable 2FA'}
        </button>
      </div>
    </form>
  );
}
